// client/src/pages/UserDashboard.js

import React, { useEffect, useState } from 'react';
import axios from 'axios';

export default function UserDashboard() {
  const [isAllowed, setIsAllowed] = useState(false);
  const [orders, setOrders] = useState([]);
  const [userInfo, setUserInfo] = useState(null);

  // Form fields
  const [userNameInput, setUserNameInput] = useState('');
  const [userEmailInput, setUserEmailInput] = useState('');
  const [addressInput, setAddressInput] = useState('');
  const [phoneInput, setPhoneInput] = useState('');

  // For changing password
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userString = localStorage.getItem('userInfo');
    if (!token || !userString) {
      window.location.href = '/login';
      return;
    }

    setIsAllowed(true);
    const userObj = JSON.parse(userString);
    setUserInfo(userObj);

    // Fill input fields from user info
    setUserNameInput(userObj.name || '');
    setUserEmailInput(userObj.email || '');
    setAddressInput(userObj.address || '');
    setPhoneInput(userObj.phone || '');

    // Fetch user's orders
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then((res) => setOrders(res.data))
      .catch((err) => console.error(err));
  }, []);

  const handleUpdateProfile = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const { data } = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/user/profile`,
        {
          name: userNameInput,
          email: userEmailInput,
          address: addressInput,
          phone: phoneInput
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      // Update localStorage
      localStorage.setItem('userInfo', JSON.stringify(data));
      setUserInfo(data);
      alert('Profile updated successfully');
    } catch (err) {
      console.error(err);
      alert('Failed to update profile');
    }
  };

  const handleChangePassword = async () => {
    setPasswordMessage('');
    setPasswordError('');

    if (!oldPassword || !newPassword) {
      setPasswordError('Please fill out both old and new password fields.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirm password do not match.');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const { data } = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/user/change-password`,
        { oldPassword, newPassword },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setPasswordMessage(data.message || 'Password changed successfully');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error(err);
      setPasswordError(err.response?.data?.message || 'Error changing password');
    }
  };

  if (!isAllowed) {
    return <div className="p-4 mt-16">Redirecting to login...</div>;
  }

  return (
    <div className="max-w-5xl mx-auto p-4 mt-16 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-bold mb-6">User Dashboard</h1>

      {/* Profile Section */}
      <div className="bg-white p-4 shadow rounded mb-8">
        <h2 className="text-xl font-semibold mb-2">My Profile</h2>
        {userInfo && (
          <>
            <label className="block mt-4 font-medium">Name:</label>
            <input
              type="text"
              className="border p-2 w-full"
              value={userNameInput}
              onChange={(e) => setUserNameInput(e.target.value)}
            />

            <label className="block mt-4 font-medium">Email:</label>
            <input
              type="email"
              className="border p-2 w-full"
              value={userEmailInput}
              onChange={(e) => setUserEmailInput(e.target.value)}
            />

            <label className="block mt-4 font-medium">Address:</label>
            <input
              type="text"
              className="border p-2 w-full"
              value={addressInput}
              onChange={(e) => setAddressInput(e.target.value)}
            />

            <label className="block mt-4 font-medium">Phone:</label>
            <input
              type="text"
              className="border p-2 w-full"
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value)}
            />

            <button
              onClick={handleUpdateProfile}
              className="mt-4 bg-primaryGreen text-white px-4 py-2 rounded hover:bg-green-500"
            >
              Update Profile
            </button>
            <p className="text-sm text-gray-500 mt-2">
              Updating address or phone won't change past orders.
            </p>
          </>
        )}
      </div>

      {/* Change Password Section */}
      <div className="bg-white p-4 shadow rounded mb-8">
        <h2 className="text-xl font-semibold mb-2">Change Password</h2>
        <label className="block mt-4 font-medium">Old Password:</label>
        <input
          type="password"
          className="border p-2 w-full"
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
        />

        <label className="block mt-4 font-medium">New Password:</label>
        <input
          type="password"
          className="border p-2 w-full"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />

        <label className="block mt-4 font-medium">Confirm New Password:</label>
        <input
          type="password"
          className="border p-2 w-full"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />

        <button
          onClick={handleChangePassword}
          className="mt-4 bg-primaryGreen text-white px-4 py-2 rounded hover:bg-green-500"
        >
          Update Password
        </button>

        {passwordMessage && (
          <p className="mt-2 text-green-600">{passwordMessage}</p>
        )}
        {passwordError && <p className="mt-2 text-red-500">{passwordError}</p>}
      </div>

      {/* Orders Section */}
      <div className="bg-white p-4 shadow rounded">
        <h2 className="text-xl font-semibold mb-4">My Orders</h2>
        {orders.length === 0 ? (
          <p>No orders found.</p>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div key={order._id} className="border p-3 rounded bg-white">
                <p className="mb-1">
                  <strong>Order ID:</strong> {order._id}
                </p>
                <p className="mb-1">
                  <strong>Total:</strong> ${order.totalPrice}
                </p>
                <p className="mb-1">
                  <strong>Address at Purchase:</strong> {order.shippingAddress}
                </p>
                <p className="mb-1">
                  <strong>Payment Status:</strong> {order.paymentStatus}
                </p>

                {/* Show ordered items */}
                <p className="mt-2 font-medium">Ordered Products:</p>
                {order.orderItems && order.orderItems.length > 0 ? (
                  <ul className="list-disc list-inside mt-1">
                    {order.orderItems.map((item) => {
                      // If populated, item.product could have name, etc.
                      const productName = item.product
                        ? item.product.name
                        : 'Unknown Product';
                      return (
                        <li key={item._id}>
                          {productName} (x {item.quantity}) = $
                          {item.price * item.quantity}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p>No items found in this order.</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
